import * as R from 'ramda';
import {
  DRUID_TO_HUMAN_READABLE,
  CORE_EVENT_NAMES,
  STANDARD_EVENT_NAMES_WITH_LOCAL_CURRENCY,
  FILTER_DIMENSIONS,
  CONDITIONAL_DRUID_TO_HUMAN_READABLE,
} from '../../../support/eo_mapping';
import {
  CALCULATION_TOPICS_TO_HUMAN_READABLE,
  LOCAL_CURRENCY_EVENTS_TO_HUMAN_READABLE,
} from '../../../support/eo_mapping/event_names';
import { SK_ADNETWORK_DATASOURCE } from '../../../support/eo_mapping/datasources';
import {
  getCustomEventName,
  getLabelFromEventDescription,
  isCustomEvent,
  parseEventDescriptionFromEventKeyOrUndefined,
} from '../../../support/utils';
import type { DruidExplanation } from '../../../../../../shared/types/druidTypes';

/*
 * Maps an array of EO key strings to an array of objects, each with 'label'
 * and 'value' properties, where the label is the display name for the
 * corresponding key.
 *
 * @param {array} Array of EO key strings
 * @return {array} Array of objects, each with 'label' and 'value' properties,
 * where the label is the display name corresponding to that key
 */
export const toSelectFormatEventDatasourceMap = function (options, labelsOverride = {}) {
  return options.map((option) => {
    if (labelsOverride[option]) {
      return {
        value: option,
        label: labelsOverride[option],
      };
    }

    if (isCustomEvent(option)) {
      return {
        label: getCustomEventName(option),
        value: option,
      };
    }

    // using datasource as value (i.e. eo_install)
    // using human readable name as label (i.e. installs)
    if (CORE_EVENT_NAMES[option]) {
      const coreEventDatasource = CORE_EVENT_NAMES[option];
      return {
        value: coreEventDatasource,
        label: DRUID_TO_HUMAN_READABLE[coreEventDatasource],
      };
    }

    const shouldMapOptionToHumanReadable =
      option === SK_ADNETWORK_DATASOURCE ||
      LOCAL_CURRENCY_EVENTS_TO_HUMAN_READABLE[option] ||
      CALCULATION_TOPICS_TO_HUMAN_READABLE[option];

    if (shouldMapOptionToHumanReadable) {
      return {
        value: option,
        label: DRUID_TO_HUMAN_READABLE[option],
      };
    }

    const eventDescription = parseEventDescriptionFromEventKeyOrUndefined(option);

    if (eventDescription) {
      return {
        value: option,
        label: getLabelFromEventDescription(eventDescription),
      };
    }

    // can't use datasource as value since multiple event names per datasource
    // currently not mapping to human readable labels
    return {
      value: option,
      label: option,
    };
  });
};

/*
 * Identical to toSelectFormatEventDatasourceMap with 2 differences:
 * 1. custom events are not prepended with eo_custom_event
 * 2. core events are not translated to their eo names (keep 'OPEN' as 'OPEN' instead of 'eo_open')
 */
export const toSelectFormatCohortEventDatasourceMap = R.map((option) => {
  return {
    value: option,
    label: option,
  };
});
export const toSelectFormatDimensionMap = (option: string, labelMap: Record<string, any> = DRUID_TO_HUMAN_READABLE) => {
  const label = labelMap[option] || getCustomEventName(option);
  return {
    label,
    value: option,
  };
};

export function toSelectValue(
  value: string,
  labelMap: Record<string, any> = FILTER_DIMENSIONS,
  unclearableValue: boolean = false,
  dimension?: string
) {
  let label = labelMap[value] || value;

  if (dimension && Object.keys(CONDITIONAL_DRUID_TO_HUMAN_READABLE).includes(dimension)) {
    label = CONDITIONAL_DRUID_TO_HUMAN_READABLE[dimension][value] || label;
  }

  // make date_range unclearable in analytics table CompareBy dropdowns
  if (value === 'date_range' || unclearableValue) {
    return {
      label,
      value,
      unclearableValue: true,
    };
  }

  return {
    label,
    value,
  };
}

/*
 * Translates all filter options from just arrays of EO strings to arrays of
 * objects each with a display label.
 *
 * @param {object} Object with a property for each potential filter, the value
 * of which is an array of EO strings representing each option for that filter.
 * @return {object} Object with same original keys, but each value is now an
 * array of objects with display name 'label' and 'value' properties.
 */
export const formatFilterOptions = (filters: Record<string, any>, labelMap?: Record<string, any>) => {
  const filterArrays = Object.values(filters);
  const filterKeys = Object.keys(filters);
  return filterArrays.reduce((formattedFilterOptions: {}, values: any, index: number) => {
    if (!values || !Array.isArray(values)) {
      return formattedFilterOptions;
    }

    const key = filterKeys[index];
    const map = labelMap || CONDITIONAL_DRUID_TO_HUMAN_READABLE[key];
    formattedFilterOptions[key] = values.map((filterOption) => toSelectFormatDimensionMap(filterOption, map));
    return formattedFilterOptions;
  }, {});
};

/**
 * turns and object containing druid filter types as keys into select format
 * @param {Object} filters
 * @return {Array} react-select formatted list of filter type options
 */
export const objectKeysToSelectFormat = (filters: Record<string, any>, labelMap?: Record<string, any>) => {
  return Object.keys(filters).map((dimension) => {
    return toSelectFormatDimensionMap(dimension, labelMap);
  });
};

export const getDataSourcesFromEvent = (event: string) => {
  let dataSource = STANDARD_EVENT_NAMES_WITH_LOCAL_CURRENCY[event];

  if (!dataSource) {
    dataSource = isCustomEvent(event) ? 'eo_custom_event' : event;
  }

  return dataSource;
};
// sort dropdown options alphabetically by label
export const sortByLabel = <T extends { label: string }>(dropdownOptions: Array<T>): Array<T> =>
  dropdownOptions.sort((a, b) => (a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1));

export const getUnselectedFilterTypes = (displayFilters: Record<string, any>[], filter_types: Record<string, any>[]) => {
  const isNotDisplayFilter = (filterType) => {
    for (const index in displayFilters) {
      if (filterType.value === displayFilters[index].dimension) {
        return false;
      }
    }

    return true;
  };

  return filter_types.filter(isNotDisplayFilter);
};

type BlockedDimension = {
  dimensions: string[];
  blockedBy: string;
};

function getDimensionsFromConflict(conflict, api, includePotential) {
  let dimensions = [];

  for (const conflictApi in conflict.conflict_with) {
    if (conflictApi !== api) {
      dimensions = conflict.conflict_with[conflictApi];
    }
  }

  if (includePotential) {
    for (const conflictApi in conflict.potential_conflict_with) {
      if (conflictApi !== api) {
        dimensions = conflict.potential_conflict_with[conflictApi];
      }
    }
  }

  return dimensions;
}

function processAPIsInSource(source, explanation, includePotential): BlockedDimension[] {
  const blockedDimensions: BlockedDimension[] = [];

  for (const api of Object.keys(explanation.cost.conflicts[source])) {
    const conflict = explanation.cost.conflicts[source][api];
    const blockedBy = conflict.dimensions[0];
    const dimensions = getDimensionsFromConflict(conflict, api, includePotential);

    blockedDimensions.push({ dimensions, blockedBy });
  }

  return blockedDimensions;
}

export const getBlockedDimensions = ({
  explanation,
  includePotential = false,
}: {
  explanation?: DruidExplanation;
  includePotential?: boolean;
}): BlockedDimension[] => {
  let blockedDimensions: BlockedDimension[] = [];
  if (!explanation) {
    return [];
  }

  const apis = Object.keys(explanation);

  for (const api of apis) {
    for (const source of Object.keys(explanation[api].conflicts)) {
      blockedDimensions = blockedDimensions.concat(processAPIsInSource(source, explanation, includePotential));
    }
  }

  return blockedDimensions;
};

export const getRelatedMetrics = (explanation?: DruidExplanation): string[] => {
  if (!explanation) {
    return [];
  }

  let relatedMetrics: string[] = [];

  const apis = Object.keys(explanation);

  for (const api of apis) {
    for (const metric of Object.keys(explanation[api].cost_related_metrics)) {
      relatedMetrics = relatedMetrics.concat(explanation[api].cost_related_metrics[metric]);
    }
  }

  return relatedMetrics;
};
